
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Target, MoveRight, Layers3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CTAButton from '../CTAButton';

const OurVision = () => {
    const navigate = useNavigate();
    const beliefs = [
        "operational efficiency",
        "business scalability",
        "workforce productivity",
        "organizational stability",
        "long-term growth performance"
    ];

    const highlights = [
        {
            sh: "Workforce-Focused HR Systems",
            desc: "Designed for operational scalability",
            color: "#3b82f6"
        },
        {
            sh: "Recruitment Intelligence Models",
            desc: "Built for faster hiring execution",
            color: "#8b5cf6"
        },
        {
            sh: "Integrated Workforce Solutions",
            desc: "Staffing + Payroll + Compliance + RPO",
            color: "#06b6d4"
        },
        {
            sh: "Multi-Industry Hiring Expertise",
            desc: "Serving modern business ecosystems",
            color: "#10b981"
        }
    ];

    // Variants for staggered children
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -30 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
    };

    return (
        <section className="py-10 bg-white overflow-hidden relative">
            {/* --- ANIMATED BACKGROUND NODES --- */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
                {[...Array(5)].map((_, i) => (
                    <motion.div
                        key={i}
                        animate={{
                            y: [0, -100, 0],
                            x: [0, 50, 0],
                            scale: [1, 1.2, 1],
                        }}
                        transition={{
                            duration: 10 + i * 2,
                            repeat: Infinity,
                            ease: "linear"
                        }}
                        className="absolute w-64 h-64 bg-blue-50 rounded-full blur-[80px]"
                        style={{
                            top: `${i * 20}%`,
                            left: `${i * 15}%`,
                        }}
                    />
                ))}
            </div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
                    
                    {/* LEFT SIDE: THE VISION CONTENT */}
                    <motion.div 
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        className="lg:w-[55%]"
                    >
                        <motion.span variants={itemVariants} className="text-blue-600 font-black tracking-[0.4em] text-[10px] uppercase block mb-4">
                            STRATEGIC ROADMAP
                        </motion.span>
                        
                        <motion.h2 variants={itemVariants} className="text-3xl md:text-5xl font-bold text-slate-900 syne leading-[1.1] mb-12">
                            The Vision Behind <br/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Merida HR Consulting</span>
                        </motion.h2>
                        
                        <div className="space-y-8 text-slate-500 text-lg md:text-lg leading-relaxed mb-12">
                            <motion.p variants={itemVariants}>
                                Businesses today are scaling faster than their internal hiring infrastructure can support. 
                                <a href="https://www.talentlyft.com/blog/what-are-the-risks-of-a-delayed-recruitment-process" target="_blank" rel="noreferrer" className="text-blue-600 underline">Recruitment delays and compliance risks</a> have become common growth barriers.
                            </motion.p>
                            <motion.div variants={itemVariants} className="p-8 rounded-[2rem] bg-blue-600 text-white shadow-2xl shadow-blue-900/20 relative overflow-hidden group">
                                <p className="relative z-10 font-bold">
                                    We help organizations transition from fragmented operations into streamlined workforce ecosystems capable of supporting sustainable growth.
                                </p>
                                <div className="absolute -right-4 -bottom-4 opacity-10 group-hover:scale-110 transition-transform duration-700">
                                    <Layers3 size={120} />
                                </div>
                            </motion.div>
                        </div>

                        {/* BELIEF MATRIX WITH SPRING ANIMATION */}
                        <motion.div variants={itemVariants} className="bg-blue-50/20 p-10 rounded-[3rem] border border-blue-100/50">
                            <p className="text-slate-900 font-black uppercase tracking-widest text-[11px] mb-8">
                                Our strategy contributes to:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {beliefs.map((item, index) => (
                                    <motion.div 
                                        key={index}
                                        whileHover={{ x: 10 }}
                                        className="flex items-center gap-4 group cursor-default"
                                    >
                                        <motion.div 
                                            initial={{ scale: 0 }}
                                            whileInView={{ scale: 1 }}
                                            transition={{ type: 'spring', stiffness: 300, delay: index * 0.1 }}
                                            className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-blue-600 shadow-md group-hover:bg-blue-600 group-hover:text-white transition-colors"
                                        >
                                            <CheckCircle2 size={16} />
                                        </motion.div>
                                        <span className="text-slate-700 font-bold text-sm capitalize">{item}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* RIGHT SIDE: INTERACTIVE NUMERIC DECK */}
                    <div className="lg:w-[45%] flex flex-col gap-8 relative">
                        
                        {/* Animated connector line */}
                        <motion.div 
                            initial={{ height: 0 }}
                            whileInView={{ height: '80%' }}
                            transition={{ duration: 1.5, delay: 0.5 }}
                            className="absolute left-12 top-20 w-[1px] bg-gradient-to-b from-blue-500 via-purple-500 to-transparent hidden lg:block"
                        />

                        {highlights.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: 100 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ 
                                    type: "spring", 
                                    stiffness: 100, 
                                    damping: 20, 
                                    delay: index * 0.2 
                                }}
                                whileHover={{ 
                                    scale: 1.02, 
                                    rotateY: -5,
                                    perspective: 1000 
                                }}
                                className="group relative bg-[#01014C] p-7 rounded-[2.5rem] border border-blue-500/30 hover:border-blue-400 shadow-xl shadow-blue-900/40 hover:shadow-2xl hover:shadow-blue-500/20 cursor-pointer transition-all duration-300"
                            >
                                {/* Inner Glow */}
                                <div 
                                    className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-[2.5rem]" 
                                    style={{ background: `radial-gradient(circle at center, ${item.color}, transparent)` }}
                                />

                                <div className="relative z-10 flex items-start gap-8">
                                    <div className="flex flex-col items-center">
                                        <span className="text-5xl font-black syne text-transparent bg-clip-text bg-gradient-to-b from-white to-white/10">
                                            0{index + 1}
                                        </span>
                                    </div>

                                    <div>
                                        <h4 className="text-white text-xl font-bold mb-3 syne group-hover:text-blue-400 transition-colors">
                                            {item.sh}
                                        </h4>
                                        <p className="text-blue-100/40 font-medium text-sm leading-relaxed">
                                            {item.desc}
                                        </p>
                                        
                                        <div className="mt-6 flex items-center gap-3">
                                            <div className="h-[1px] w-8 bg-blue-500/50 group-hover:w-16 transition-all duration-500" />
                                            <span className="text-[9px] font-black text-blue-400 uppercase tracking-[0.2em]">Pillar</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                        {/* FINAL TARGET ANIMATION */}
                        {/* <motion.div 
                            initial={{ scale: 0.8, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            className="mt-4 p-10 rounded-[2.5rem] border-2 border-dashed border-slate-200 flex flex-col items-center text-center justify-center bg-white/50 backdrop-blur-sm"
                        >
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                className="mb-6"
                            >
                                <Target className="text-blue-600/20" size={60} />
                            </motion.div>
                            <p className="text-slate-400 font-black uppercase tracking-widest text-[10px]">
                                Strategic Transformation Foundation
                            </p>
                        </motion.div> */}
                    </div>

                </div>

                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="mt-16 flex justify-center w-full"
                >
                    <CTAButton 
                        text="Discover Our Vision"
                        onClick={() => navigate('/contact')}
                        type="primary"
                    />
                </motion.div>

            </div>
        </section>
    );
};

export default OurVision;